import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api-client";

export type AccountStatus = "PENDING_QR" | "ACTIVE" | "NEEDS_RELOGIN" | "PAUSED" | "DISABLED";

export type ZaloAccount = {
    id: string;
    zaloUserId: string | null;
    displayName: string;
    avatarUrl: string | null;
    phone: string | null;
    status: AccountStatus;
    minDelayMs: number;
    maxDelayMs: number;
    concurrency: number;
    maxPerHour: number;
    maxPerDay: number;
    activeFrom: string;
    activeTo: string;
    timezone: string;
    consecutiveFailures: number;
    lastActiveAt: string | null;
    lastError: string | null;
    groupCount: number;
};

export type AccountSettings = Pick<
    ZaloAccount,
    "minDelayMs" | "maxDelayMs" | "concurrency" | "maxPerHour" | "maxPerDay" | "activeFrom" | "activeTo"
>;

export const ACCOUNTS_KEY = ["accounts"] as const;

export function useAccounts() {
    return useQuery({
        queryKey: ACCOUNTS_KEY,
        queryFn: () => api.get<ZaloAccount[]>("/api/accounts"),
    });
}

function useAccountMutation<TVariables>(fn: (variables: TVariables) => Promise<unknown>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
    });
}

export function useUpdateAccount() {
    return useAccountMutation(({ id, settings }: { id: string; settings: Partial<AccountSettings> }) =>
        api.patch(`/api/accounts/${id}`, settings),
    );
}

export function usePauseAccount() {
    return useAccountMutation(({ id, paused }: { id: string; paused: boolean }) =>
        api.post(`/api/accounts/${id}/${paused ? "pause" : "resume"}`),
    );
}

export function useDeleteAccount() {
    return useAccountMutation(({ id }: { id: string }) => api.delete(`/api/accounts/${id}`));
}
